import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import MapControlMap from './MapControlMap';
import './MapControlChart.css';

/** Uppspelningstakt: millisekunder per sekund i rundan (4× realtid). */
const PLAY_MS = 250;

/** Lagren nedifrån och upp: CT, omstritt, neutralt, T (tillsammans 100 %). */
const LAYERS = ['ct', 'contested', 'neutral', 't'];

/**
 * Avrundar en andel (0–1) till hela procent.
 *
 * @param {number} fraction Andel mellan 0 och 1.
 * @returns {number} Procent, 0–100.
 */
function toPct(fraction) {
  return Math.round((fraction || 0) * 1000) / 10;
}

/**
 * Bygger diagramrader för en runda och en modell.
 *
 * Backend skickar `[ct, t, contested]` per sampel (en per `sampleSeconds`
 * från freeze end); neutralt är resten upp till 100 %.
 *
 * @param {Array<Array<number>>} points Sampel för rundan.
 * @param {number} sampleSeconds Sekunder mellan sampel.
 * @returns {Array<{second: number, ct: number, t: number, contested: number, neutral: number}>}
 *   En rad per sampel.
 */
function buildRows(points, sampleSeconds) {
  return (points || []).map(([ct, t, contested], index) => {
    const row = { second: index * sampleSeconds, ct: toPct(ct), t: toPct(t), contested: toPct(contested) };
    row.neutral = Math.max(0, Math.round((100 - row.ct - row.t - row.contested) * 10) / 10);
    return row;
  });
}

/**
 * Medelvärde per lager över en rundas rader.
 *
 * @param {Array<object>} rows Rader från `buildRows`.
 * @returns {{ct: number, t: number, contested: number}} Medelandel i procent.
 */
function averages(rows) {
  const n = rows.length || 1;
  const sum = (key) => rows.reduce((acc, r) => acc + r[key], 0);
  return {
    ct: Math.round(sum('ct') / n),
    t: Math.round(sum('t') / n),
    contested: Math.round(sum('contested') / n),
  };
}

/**
 * Tooltip för kartkontrollgrafen — värden i textfärg, färgen bärs av en markör.
 *
 * @param {{active?: boolean, payload?: Array<object>, label?: number}} props Från Recharts.
 * @returns {JSX.Element|null} Tooltip-innehållet.
 */
function MapControlTooltip({ active, payload, label }) {
  const { t } = useTranslation();
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload;
  return (
    <div className="map-control-tooltip">
      <div className="map-control-tooltip-title">{t('mapControl.secondLabel', { second: label })}</div>
      {['t', 'neutral', 'contested', 'ct'].map((key) => (
        <div key={key} className="map-control-tooltip-row">
          <span className={`map-control-swatch map-control-swatch-${key}`} />
          <span className="map-control-tooltip-name">{t(`mapControl.${key}`)}</span>
          <span className="map-control-tooltip-value">{row[key]}%</span>
        </div>
      ))}
    </div>
  );
}

/**
 * MapControlChart — kartkontroll per runda (awpy 3 steg 3).
 *
 * Visar hur stor del av kartan CT respektive T kontrollerade under rundan, en
 * punkt per sekund från freeze end. Två modeller: territorium (vem når först,
 * standard) och sikt (vem ser). Staplat till 100 %: CT nedifrån, T uppifrån,
 * omstritt och neutralt däremellan. Komponenten renderar ingenting utan data,
 * så matchsidan ser ut som förut för matcher som saknar kartkontroll.
 *
 * Ovanför grafen visas kartan (MapControlMap) vid vald sekund. Sekunden väljs med
 * reglaget, play-knappen eller genom att hålla muspekaren över grafen; en lodrät
 * linje i grafen visar var kartan står.
 *
 * @param {{
 *   matchId: string|number,
 *   mapControl: Object<string, {methods: string[], sample_seconds: number,
 *     rounds: Array<{round: number, winner: string, reachability?: Array, vision?: Array}>}>,
 *   activeMap: string|number,
 *   playerNames?: Object<string, string>,
 * }} props Matchens id, `mapControl` per kartnummer (från `/api/stats/match/{id}`) och vald
 *   karta (`'total'` eller index) från matchsidans kartflikar, och sajtens spelarnamn per
 *   steamid för kartans tooltip.
 * @returns {JSX.Element|null} Panelen, eller null utan data.
 */
const MapControlChart = ({ matchId, mapControl, activeMap, playerNames }) => {
  const { t } = useTranslation();
  const [method, setMethod] = useState('reachability');
  const [roundIndex, setRoundIndex] = useState(0);
  const [sampleIndex, setSampleIndex] = useState(0);
  const [playing, setPlaying] = useState(false);

  const keys = Object.keys(mapControl || {}).sort((a, b) => Number(a) - Number(b));
  const mapKey = keys.includes(String(activeMap)) ? String(activeMap) : keys[0];
  const data = mapKey !== undefined ? mapControl[mapKey] : null;
  const rounds = data?.rounds || [];
  const methods = data?.methods || [];
  const activeMethod = methods.includes(method) ? method : methods[0];
  const round = rounds[Math.min(roundIndex, Math.max(rounds.length - 1, 0))];

  const rows = useMemo(
    () => (round && activeMethod ? buildRows(round[activeMethod], data.sample_seconds || 1) : []),
    [round, activeMethod, data],
  );

  // Ny runda → börja om från freeze end.
  useEffect(() => { setSampleIndex(0); setPlaying(false); }, [roundIndex, mapKey]);

  // Uppspelning: en sekund i rundan per PLAY_MS, stannar vid rundans slut.
  useEffect(() => {
    if (!playing) return undefined;
    const id = setInterval(() => {
      setSampleIndex((i) => {
        if (i >= rows.length - 1) { setPlaying(false); return i; }
        return i + 1;
      });
    }, PLAY_MS);
    return () => clearInterval(id);
  }, [playing, rows.length]);

  if (!rounds.length || !activeMethod) return null;
  const avg = averages(rows);
  const current = rows[Math.min(sampleIndex, rows.length - 1)];

  return (
    <div className="map-control">
      <div className="map-control-header">
        <span className="map-control-title">{t('mapControl.title')}</span>
        <div className="map-control-methods" role="tablist">
          {methods.map((m) => (
            <button
              key={m}
              type="button"
              role="tab"
              aria-selected={m === activeMethod}
              className={`map-control-method ${m === activeMethod ? 'active' : ''}`}
              onClick={() => setMethod(m)}
            >
              {t(`mapControl.${m}`)}
            </button>
          ))}
        </div>
      </div>
      <p className="map-control-help">{t(`mapControl.${activeMethod}Help`)}</p>

      <div className="map-control-rounds" aria-label={t('mapControl.rounds')}>
        {rounds.map((r, index) => (
          <button
            key={r.round}
            type="button"
            className={`map-control-round map-control-round-${r.winner || 'none'} ${index === roundIndex ? 'active' : ''}`}
            onClick={() => setRoundIndex(index)}
            title={r.winner ? t('mapControl.roundWonBy', { round: r.round, side: t(`mapControl.${r.winner}`) }) : ''}
          >
            {r.round}
          </button>
        ))}
      </div>

      <MapControlMap
        matchId={matchId}
        mapNumber={mapKey}
        roundNumber={round.round}
        method={activeMethod}
        sampleIndex={sampleIndex}
        playerNames={playerNames}
      />

      <div className="map-control-scrubber">
        <button
          type="button"
          className="map-control-play"
          onClick={() => {
            if (!playing && sampleIndex >= rows.length - 1) setSampleIndex(0);
            setPlaying((p) => !p);
          }}
          aria-label={playing ? t('mapControl.pause') : t('mapControl.play')}
        >
          {playing ? '❚❚' : '▶'}
        </button>
        <input
          type="range"
          className="map-control-slider"
          min={0}
          max={Math.max(rows.length - 1, 0)}
          value={Math.min(sampleIndex, Math.max(rows.length - 1, 0))}
          onChange={(e) => { setPlaying(false); setSampleIndex(Number(e.target.value)); }}
          aria-label={t('mapControl.secondSlider')}
        />
        <span className="map-control-time">
          {current ? t('mapControl.secondLabel', { second: current.second }) : ''}
        </span>
      </div>

      <div className="map-control-legend">
        {['ct', 'contested', 'neutral', 't'].map((key) => (
          <span key={key} className="map-control-legend-item">
            <span className={`map-control-swatch map-control-swatch-${key}`} />
            {t(`mapControl.${key}`)}
          </span>
        ))}
      </div>

      <div className="map-control-chart">
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart
            data={rows}
            margin={{ top: 8, right: 12, left: 0, bottom: 0 }}
            onMouseMove={(state) => {
              if (state && state.activeTooltipIndex !== undefined && state.activeTooltipIndex !== null) {
                setPlaying(false);
                setSampleIndex(state.activeTooltipIndex);
              }
            }}
          >
            <XAxis
              dataKey="second"
              tickLine={false}
              tickFormatter={(s) => `${s}s`}
              minTickGap={24}
            />
            <YAxis
              domain={[0, 100]}
              ticks={[0, 50, 100]}
              tickLine={false}
              width={38}
              tickFormatter={(v) => `${v}%`}
            />
            <Tooltip content={<MapControlTooltip />} />
            {LAYERS.map((key) => (
              <Area
                key={key}
                className={`map-control-area map-control-area-${key}`}
                type="monotone"
                dataKey={key}
                stackId="control"
                isAnimationActive={false}
                activeDot={false}
              />
            ))}
            {current && (
              <ReferenceLine x={current.second} className="map-control-cursor" ifOverflow="extendDomain" />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <p className="map-control-summary">
        {t('mapControl.summary', {
          round: round.round,
          ct: avg.ct,
          t: avg.t,
          contested: avg.contested,
          winner: round.winner ? t(`mapControl.${round.winner}`) : '—',
        })}
      </p>
    </div>
  );
};

export default MapControlChart;
